/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../App";
import { useUsers } from "../hooks/useUsers";

import { calculateAge } from "../utils/zodiac";

import { Search, Loader2, SlidersHorizontal, X, RefreshCw, Users, Lock } from "lucide-react";
import UserCard from "../components/browse/UserCard";
import FilterPanel from "../components/browse/FilterPanel";
import { TAIWAN_CITIES } from "../utils/taiwanCities";
import { INTERESTS } from "../utils/profileConstants";

import { supabase } from '../utils/supabaseClient';
import { awardPoints } from '../utils/pointsService';
import { useModal } from '../context/ModalContext';

export default function Browse() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, blockedIds, setBlockedIds } = useContext(UserContext);
  const { data, isLoading, isFetching, refetch } = useUsers(currentUser?.id, blockedIds);
  const { showAlert } = useModal();

  // Re-fetch blockedIds from DB on mount to reflect any admin changes
  useEffect(() => {
    if (!currentUser?.id) return;
    supabase
      .from('blocked_users')
      .select('blocker_id, blocked_id')
      .or(`blocker_id.eq.${currentUser.id},blocked_id.eq.${currentUser.id}`)
      .then(({ data: blockedData }) => {
        const ids = (blockedData || []).map(row =>
          row.blocker_id === currentUser.id ? row.blocked_id : row.blocker_id
        );
        setBlockedIds(ids);
      });
  }, [currentUser?.id]);

  const users = data?.users || [];
  const friendships = data?.friendships || [];

  const DEFAULT_FILTERS = {
    gender: "all",
    age_range: "all",
    city: "all",
    interest: "all",
    sort_by: "latest"
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

  // Temp filter states for mobile overlay
  const [tempFilters, setTempFilters] = useState({ ...DEFAULT_FILTERS });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Count active filters
  const activeFilterCount = [
    filters.gender !== "all",
    filters.age_range !== "all",
    filters.city !== "all",
    filters.interest !== "all",
    filters.sort_by !== "latest"
  ].filter(Boolean).length;

  const openMobileFilter = () => {
    setTempFilters({ ...filters });
    setShowMobileFilter(true);
  };

  const applyMobileFilter = () => {
    setFilters({ ...tempFilters });
    setShowMobileFilter(false);
  };

  const resetMobileFilter = () => {
    setTempFilters({ ...DEFAULT_FILTERS });
  };

  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
  };

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.profile.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.profile.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.profile.occupation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.profile.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.gender !== "all") {
      filtered = filtered.filter(user => user.profile.gender === filters.gender);
    }

    if (filters.age_range !== "all") {
      const [min, max] = filters.age_range.split("-").map(Number);
      filtered = filtered.filter(user => {
        const userAge = user.profile.birthday ? calculateAge(user.profile.birthday) : user.profile.age;
        if (!userAge) return false;
        return max ? userAge >= min && userAge <= max : userAge >= min;
      });
    }

    if (filters.city !== "all") {
      filtered = filtered.filter(user => user.profile.city === filters.city);
    }

    if (filters.interest !== "all") {
      filtered = filtered.filter(user => (user.profile.interests || []).includes(filters.interest));
    }

    switch (filters.sort_by) {
      case "age_asc":
        filtered.sort((a, b) => {
          const ageA = a.profile.birthday ? calculateAge(a.profile.birthday) : (a.profile.age || 0);
          const ageB = b.profile.birthday ? calculateAge(b.profile.birthday) : (b.profile.age || 0);
          return ageA - ageB;
        });
        break;
      case "age_desc":
        filtered.sort((a, b) => {
          const ageA = a.profile.birthday ? calculateAge(a.profile.birthday) : (a.profile.age || 0);
          const ageB = b.profile.birthday ? calculateAge(b.profile.birthday) : (b.profile.age || 0);
          return ageB - ageA;
        });
        break;
      default: break;
    }

    return filtered;
  }, [users, searchTerm, filters]);

  const handleAddFriend = async (friendId) => {
    if (!currentUser) return;

    const existingStatus = getFriendshipStatus(friendId);
    if (existingStatus !== 'none') {
      if (existingStatus === 'pending') showAlert('提示', "好友邀請已送出，請等待對方回應");
      else if (existingStatus === 'accepted') showAlert('提示', "你們已經是好友了");
      return;
    }

    try {
      const { error } = await supabase.from('friendships').insert({
        user_id: currentUser.id,
        friend_id: friendId,
        requested_by: currentUser.id,
        status: 'pending'
      });

      if (error) {
        if (error.code === '23505') showAlert('提示', '好友關係已存在'); // Unique constraint violation
        else showAlert('錯誤', error.message || "發送失敗，請重試");
        return;
      }

      showAlert('成功', "好友邀請已送出！");
      refetch();
    } catch (error) {
      console.error("發送好友邀請失敗:", error);
      showAlert('錯誤', "網路錯誤，請稍後重試");
    }
  };

  const getFriendshipStatus = (otherUserId) => {
    if (!currentUser) return 'none';
    const friendship = friendships.find(f =>
      (f.user._id === currentUser._id && f.friend._id === otherUserId) ||
      (f.user._id === otherUserId && f.friend._id === currentUser._id)
    );
    // Treat declined status as 'none' so users can send requests again
    if (!friendship || friendship.status === 'declined') {
      return 'none';
    }
    return friendship.status;
  };

  const getFriendshipData = (otherUserId) => {
    if (!currentUser) return null;
    const friendship = friendships.find(f =>
      (f.user._id === currentUser._id && f.friend._id === otherUserId) ||
      (f.user._id === otherUserId && f.friend._id === currentUser._id)
    );
    // Don't return friendship data for declined status
    if (friendship && friendship.status === 'declined') {
      return null;
    }
    return friendship;
  };

  const handleAcceptFriend = async (friendshipId) => {
    try {
      const { error } = await supabase.from('friendships').update({ status: 'accepted', accepted_at: new Date().toISOString() }).eq('id', friendshipId);
      if (error) {
        showAlert('錯誤', error.message || "接受好友失敗");
        return;
      }
      showAlert('成功', "已成為好友！");
      awardPoints('new_friend', friendshipId);
      refetch();
    } catch (error) {
      console.error("接受好友失敗:", error);
    }
  };

  const handleDeclineFriend = async (friendshipId) => {
    try {
      const { error } = await supabase.from('friendships').update({ status: 'declined' }).eq('id', friendshipId);
      if (error) {
        showAlert('錯誤', error.message || "拒絕好友失敗");
        return;
      }
      showAlert('提示', "已拒絕好友邀請");
      refetch();
    } catch (error) {
      console.error("拒絕好友失敗:", error);
    }
  };

  const handleCancelRequest = async (friendshipId) => {
    try {
      const { error } = await supabase.from('friendships').delete().eq('id', friendshipId);
      if (error) {
        showAlert('錯誤', error.message || "取消邀請失敗");
        return;
      }
      showAlert('提示', "已取消好友邀請");
      refetch();
    } catch (error) {
      console.error("取消邀請失敗:", error);
    }
  };

  const handleViewProfile = (userProfile, userId) => {
    // Navigate to user detail page
    navigate(`/user/${userId}`);
  };

  // Shared styles for mobile overlay
  const mobileSelectStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    outline: 'none',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none'
  };

  const mobileLabelStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    display: 'block'
  };

  const isSocialEnabled = currentUser?.profile?.is_social_enabled ?? true;

  if (!isSocialEnabled) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f6f9fc 0%, #f4f7fa 15%, #f8fafc 40%, #ffffff 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        textAlign: 'center',
        paddingBottom: '100px'
      }}>
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%', background: '#fef2f2',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px'
        }}>
          <Lock size={40} style={{ color: '#ef4444' }} />
        </div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px' }}>
          開啟交友模式，探索更多可能！
        </h2>
        <p style={{ color: '#6b7280', fontSize: '15px', lineHeight: '1.6', maxWidth: '300px', marginBottom: '32px' }}>
          你目前的狀態為「專注運動」。開啟交友模式後，你的個人資料將對其他用戶可見，並且可以開始認識新朋友！
        </p>
        <button
          onClick={() => navigate('/profile')}
          style={{
            padding: '14px 28px', borderRadius: '999px', border: 'none',
            background: '#4e8bca', color: 'white', fontSize: '16px', fontWeight: '600',
            cursor: 'pointer', boxShadow: '0 8px 25px rgba(78, 139, 202, 0.3)', transition: 'transform 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          前往開啟交友模式
        </button>
      </div>
    );
  }

  if (isLoading && users.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '1.2rem', marginLeft: '12px' }}>載入用戶中...</span>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #eef4f9 0%, #f0faf9 15%, #f8fffe 40%, #ffffff 100%)',
      padding: '0 0 80px 0'
    }}>
      {/* Gradient Header */}
      <div style={{
        background: 'linear-gradient(135deg, #4e8bca 0%, #6a9fd3 60%, #9cc2e6 100%)',
        padding: isMobile ? '28px 20px 32px' : '36px 32px 40px',
        borderRadius: '0 0 32px 32px',
        marginBottom: '0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative circles */}
        <div style={{
          position: 'absolute', top: '-30px', right: '-20px',
          width: '120px', height: '120px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)'
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '30px',
          width: '80px', height: '80px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <Users size={isMobile ? 24 : 28} style={{ color: 'rgba(255,255,255,0.9)' }} />
            <h1 style={{
              fontSize: isMobile ? '1.75rem' : '2rem',
              fontWeight: '800',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
              color: '#ffffff',
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: '1.2',
              textShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>揪好友</h1>
          </div>
          <p style={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: '0.875rem',
            margin: 0,
            fontWeight: '400',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', sans-serif",
            letterSpacing: '0.01em',
            lineHeight: '1.5'
          }}>
            與你在意的人保持聯繫
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>

        {/* Tabs: 探索新朋友 / 好友邀請 / 我的好友 */}
        <div style={{
          display: 'flex', gap: '4px', marginTop: '16px',
          background: 'rgba(255,255,255,0.9)', borderRadius: '999px', padding: '4px',
          border: '1px solid rgba(78, 139, 202, 0.08)',
          boxShadow: '0 4px 16px rgba(78, 139, 202, 0.06)'
        }}>
          <button
            style={{
              flex: 1, padding: '10px 0', borderRadius: '999px', border: 'none',
              background: '#4e8bca', color: 'white',
              fontSize: '14px', fontWeight: '700', cursor: 'default'
            }}
          >
            探索新朋友
          </button>
          <button
            onClick={() => navigate('/friends', { state: { tab: 'requests' } })}
            style={{
              flex: 1, padding: '10px 0', borderRadius: '999px', border: 'none',
              background: 'transparent', color: '#6b7280',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            好友邀請
          </button>
          <button
            onClick={() => navigate('/friends', { state: { tab: 'friends' } })}
            style={{
              flex: 1, padding: '10px 0', borderRadius: '999px', border: 'none',
              background: 'transparent', color: '#6b7280',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            我的好友
          </button>
        </div>

        {/* Search and Filter Panel - Sticky */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          paddingTop: '12px',
          paddingBottom: '8px',
          marginTop: '16px',
          marginBottom: isMobile ? '16px' : '24px'
        }}>
          <div style={{
            padding: isMobile ? '14px' : '20px 28px',
            background: 'rgba(255, 255, 255, 0.85)',
            borderRadius: isMobile ? '16px' : '20px',
            boxShadow: '0 8px 32px rgba(78, 139, 202, 0.12), 0 2px 8px rgba(0,0,0,0.04)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(78, 139, 202, 0.08)',
          }}>
            <div style={{
              display: 'flex',
              gap: isMobile ? '8px' : '20px',
              alignItems: isMobile ? 'center' : 'center',
              flexWrap: 'wrap'
            }}>
              {/* Search Input */}
              <div style={{
                position: 'relative',
                flex: 1,
                minWidth: isMobile ? '0' : '250px',
              }}>
                <Search style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '18px',
                  height: '18px',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  placeholder={isMobile ? "搜尋..." : "搜尋姓名、職業、城市或介紹內容..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '44px',
                    paddingRight: '16px',
                    paddingTop: isMobile ? '10px' : '14px',
                    paddingBottom: isMobile ? '10px' : '14px',
                    borderRadius: isMobile ? '12px' : '16px',
                    border: '1px solid #f1f5f9',
                    fontSize: isMobile ? '14px' : '15px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    fontWeight: '400'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4e8bca';
                    e.target.style.backgroundColor = 'white';
                    e.target.style.boxShadow = '0 4px 15px rgba(34, 197, 94, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Mobile: Filter Icon + Refresh */}
              {isMobile && (
                <>
                  <button
                    onClick={openMobileFilter}
                    style={{
                      position: 'relative',
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      border: activeFilterCount > 0 ? '2px solid #4e8bca' : '1px solid #e5e7eb',
                      backgroundColor: activeFilterCount > 0 ? '#eef4f9' : '#ffffff',
                      color: activeFilterCount > 0 ? '#4e8bca' : '#6b7280',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s'
                    }}
                  >
                    <SlidersHorizontal style={{ width: '20px', height: '20px' }} />
                    {activeFilterCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        backgroundColor: '#4e8bca',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '700',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => refetch()}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      border: '1px solid #f1f5f9',
                      background: '#f8fafc',
                      color: '#6b7280',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s'
                    }}
                  >
                    <RefreshCw size={18} />
                  </button>
                </>
              )}

              {/* Desktop: Full filter controls */}
              {!isMobile && (
                <>
                  <FilterPanel filters={filters} onFiltersChange={setFilters} onReset={resetFilters} isMobile={isMobile} />

                  {/* Refresh Button */}
                  <button
                    onClick={() => refetch()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      borderRadius: '12px',
                      border: '1px solid #f1f5f9',
                      background: '#f8fafc',
                      color: '#6b7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f1f5f9';
                      e.target.style.borderColor = '#e2e8f0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#f8fafc';
                      e.target.style.borderColor = '#f1f5f9';
                    }}
                  >
                    <RefreshCw size={16} />
                    重新整理
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* User Grid/List */}
        {filteredUsers.length > 0 ? (
          <div style={{
            display: 'grid',
            gap: isMobile ? '16px' : '20px',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)'
          }}>
            {filteredUsers.map((user) => (
              <UserCard
                key={user._id}
                user={user.profile}
                onAddFriend={() => handleAddFriend(user._id)}
                friendshipStatus={getFriendshipStatus(user._id)}
                friendshipData={getFriendshipData(user._id)}
                currentUserId={currentUser._id}
                viewMode={viewMode}
                onViewProfile={(userProfile) => handleViewProfile(userProfile, user._id)}
                onAcceptFriend={handleAcceptFriend}
                onDeclineFriend={handleDeclineFriend}
                onCancelRequest={handleCancelRequest}
                currentUserInterests={currentUser?.profile?.interests || []}
              />
            ))}
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '60px',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '24px'
            }}>💔</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2d3748',
              marginBottom: '12px',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
              letterSpacing: '-0.02em'
            }}>
              {users.length === 0 ? "還沒有其他用戶" : "沒有找到符合條件的用戶"}
            </h3>
            <p style={{
              color: '#6b7280',
              fontSize: '1.125rem',
              lineHeight: '1.6',
              marginBottom: 0
            }}>
              {users.length === 0
                ? "邀請朋友加入 JoinU，一起開始交友之旅！"
                : "試試調整搜尋條件或篩選器"
              }
            </p>
          </div>
        )}
      </div>

      {/* Mobile Filter Full-Page Overlay */}
      {showMobileFilter && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.3s ease-out'
        }}>
          {/* Overlay Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#1a1a1a',
              margin: 0
            }}>篩選條件</h2>
            <button
              onClick={() => setShowMobileFilter(false)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#f3f4f6',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280'
              }}
            >
              <X style={{ width: '20px', height: '20px' }} />
            </button>
          </div>

          {/* Overlay Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 20px'
          }}>
            {/* Gender */}
            <div style={{ marginBottom: '24px' }}>
              <label style={mobileLabelStyle}>性別</label>
              <select
                value={tempFilters.gender}
                onChange={(e) => handleTempFilterChange('gender', e.target.value)}
                style={mobileSelectStyle}
              >
                <option value="all">全部性別</option>
                <option value="男">男性</option>
                <option value="女">女性</option>
                <option value="其他">其他</option>
              </select>
            </div>

            {/* Age Range */}
            <div style={{ marginBottom: '24px' }}>
              <label style={mobileLabelStyle}>年齡範圍</label>
              <select
                value={tempFilters.age_range}
                onChange={(e) => handleTempFilterChange('age_range', e.target.value)}
                style={mobileSelectStyle}
              >
                <option value="all">全部年齡</option>
                <option value="18-25">18-25</option>
                <option value="26-35">26-35</option>
                <option value="36-45">36-45</option>
                <option value="46+">46+</option>
              </select>
            </div>

            {/* City */}
            <div style={{ marginBottom: '24px' }}>
              <label style={mobileLabelStyle}>縣市</label>
              <select
                value={tempFilters.city}
                onChange={(e) => handleTempFilterChange('city', e.target.value)}
                style={mobileSelectStyle}
              >
                <option value="all">所有縣市</option>
                {TAIWAN_CITIES.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Interest */}
            <div style={{ marginBottom: '24px' }}>
              <label style={mobileLabelStyle}>興趣</label>
              <select
                value={tempFilters.interest}
                onChange={(e) => handleTempFilterChange('interest', e.target.value)}
                style={mobileSelectStyle}
              >
                <option value="all">全部興趣</option>
                {INTERESTS.map(interest => (
                  <option key={interest} value={interest}>{interest}</option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div style={{ marginBottom: '24px' }}>
              <label style={mobileLabelStyle}>排序方式</label>
              <select
                value={tempFilters.sort_by}
                onChange={(e) => handleTempFilterChange('sort_by', e.target.value)}
                style={mobileSelectStyle}
              >
                <option value="latest">最新註冊</option>
                <option value="age_asc">年齡由低到高</option>
                <option value="age_desc">年齡由高到低</option>
              </select>
            </div>
          </div>

          {/* Overlay Footer */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '12px',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))'
          }}>
            <button
              onClick={resetMobileFilter}
              style={{
                flex: 1,
                padding: '14px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                color: '#6b7280',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              重置
            </button>
            <button
              onClick={applyMobileFilter}
              style={{
                flex: 2,
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: '#4e8bca',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              套用篩選
            </button>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
